import type { Profile } from '@schema/index.js';

import { STARTER_THEME } from '../state.js';

/**
 * Named theme presets surfaced in the Theme editor's Brand tab.
 *
 * Each preset is a full `Profile['theme']` value — clicking applies it
 * wholesale via `patchProfile({ theme: preset })`, same shape as the
 * Reset-to-defaults button. Presets ship a coherent light + dark
 * palette pair so customers who don't want to author colors get an
 * intentional look in one click.
 *
 * Keep the list short — choice paralysis is the failure mode here. We
 * include the MobiLauncher default so the swatch row reads as a
 * complete picker rather than "default plus alternatives."
 */
export interface ThemePreset {
  id: string;
  name: string;
  /** Two-tone swatch shown in the picker: page bg + accent. */
  swatchBg: string;
  swatchAccent: string;
  theme: Profile['theme'];
}

export const THEME_PRESETS: readonly ThemePreset[] = [
  {
    id: 'mobilauncher',
    name: 'MobiLauncher',
    swatchBg: '#F5F7FA',
    swatchAccent: '#03DAC6',
    theme: STARTER_THEME,
  },
  {
    id: 'marine',
    name: 'Marine',
    swatchBg: '#F0F4F8',
    swatchAccent: '#0EA5E9',
    theme: {
      background: '#F0F4F8',
      surface:    '#FFFFFF',
      primary:    '#0369A1',
      accent:     '#0EA5E9',
      text:       '#0C1F33',
      textMuted:  '#475569',
      darkMode:   'auto',
      paletteLight: {
        headerBg:   '#0C1F33',
        headerInk:  '#E0F2FE',
        heroBg:     '#0C1F33',
        footerBg:   '#FFFFFF',
        tileBg:     '#FFFFFF',
        tileBorder: '#CBD5E1',
        cardBg:     '#FFFFFF',
        cta:        '#0369A1',
        iconAccent: '#0EA5E9',
        divider:    '#CBD5E1',
      },
      paletteDark: {
        background: '#04111F',
        surface:    '#0C1F33',
        text:       '#E0F2FE',
        textMuted:  '#94B5D1',
        headerBg:   '#0C1F33',
        headerInk:  '#E0F2FE',
        heroBg:     '#0C1F33',
        footerBg:   '#0C1F33',
        tileBg:     '#143352',
        tileBorder: '#2A4A6B',
        cardBg:     '#0C1F33',
        cta:        '#0369A1',
        iconAccent: '#7DD3FC',
        divider:    '#2A4A6B',
      },
    },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    swatchBg: '#FFF7ED',
    swatchAccent: '#F97316',
    theme: {
      background: '#FFF7ED',
      surface:    '#FFFFFF',
      primary:    '#EA580C',
      accent:     '#F97316',
      text:       '#1F1611',
      textMuted:  '#78716C',
      darkMode:   'auto',
      paletteLight: {
        headerBg:   '#7C2D12',
        headerInk:  '#FED7AA',
        heroBg:     '#7C2D12',
        footerBg:   '#FFFFFF',
        tileBg:     '#FFFFFF',
        tileBorder: '#FBBF24',
        cardBg:     '#FFFFFF',
        cta:        '#EA580C',
        iconAccent: '#EA580C',
        divider:    '#FED7AA',
      },
      paletteDark: {
        background: '#1F1207',
        surface:    '#2A1A0E',
        text:       '#FED7AA',
        textMuted:  '#C19571',
        headerBg:   '#7C2D12',
        headerInk:  '#FED7AA',
        heroBg:     '#7C2D12',
        footerBg:   '#2A1A0E',
        tileBg:     '#3A2616',
        tileBorder: '#5A3B26',
        cardBg:     '#2A1A0E',
        cta:        '#F97316',
        iconAccent: '#FB923C',
        divider:    '#5A3B26',
      },
    },
  },
  {
    id: 'forest',
    name: 'Forest',
    swatchBg: '#F0FDF4',
    swatchAccent: '#10B981',
    theme: {
      background: '#F0FDF4',
      surface:    '#FFFFFF',
      primary:    '#047857',
      accent:     '#10B981',
      text:       '#0F1F18',
      textMuted:  '#64746B',
      darkMode:   'auto',
      paletteLight: {
        headerBg:   '#064E3B',
        headerInk:  '#D1FAE5',
        heroBg:     '#064E3B',
        footerBg:   '#FFFFFF',
        tileBg:     '#FFFFFF',
        tileBorder: '#A7F3D0',
        cardBg:     '#FFFFFF',
        cta:        '#047857',
        iconAccent: '#047857',
        divider:    '#D1FAE5',
      },
      paletteDark: {
        background: '#0A1A12',
        surface:    '#10241B',
        text:       '#D1FAE5',
        textMuted:  '#7FA993',
        headerBg:   '#064E3B',
        headerInk:  '#D1FAE5',
        heroBg:     '#064E3B',
        footerBg:   '#10241B',
        tileBg:     '#18352A',
        tileBorder: '#2E4D3D',
        cardBg:     '#10241B',
        cta:        '#10B981',
        iconAccent: '#34D399',
        divider:    '#2E4D3D',
      },
    },
  },
  {
    id: 'mono',
    name: 'Mono',
    swatchBg: '#FAFAFA',
    swatchAccent: '#525252',
    theme: {
      background: '#FAFAFA',
      surface:    '#FFFFFF',
      primary:    '#262626',
      accent:     '#525252',
      text:       '#0A0A0A',
      textMuted:  '#737373',
      darkMode:   'auto',
      paletteLight: {
        headerBg:   '#171717',
        headerInk:  '#FAFAFA',
        heroBg:     '#171717',
        footerBg:   '#FFFFFF',
        tileBg:     '#FFFFFF',
        tileBorder: '#E5E5E5',
        cardBg:     '#FFFFFF',
        cta:        '#262626',
        iconAccent: '#262626',
        divider:    '#E5E5E5',
      },
      paletteDark: {
        background: '#0A0A0A',
        surface:    '#171717',
        text:       '#FAFAFA',
        textMuted:  '#A3A3A3',
        headerBg:   '#171717',
        headerInk:  '#FAFAFA',
        heroBg:     '#171717',
        footerBg:   '#171717',
        tileBg:     '#262626',
        tileBorder: '#404040',
        cardBg:     '#171717',
        cta:        '#737373',
        iconAccent: '#A3A3A3',
        divider:    '#404040',
      },
    },
  },
];
